import { NORWAY_TIME_ZONE } from "@/v2-common/constants"
import {
  toZonedDateInput,
  zonedDayRangeToUtc,
} from "@/v2-common/utils/utils.date"
import { LEGAL_BILLING_STATUS_PENDING } from "@/v2-backoffice-client/legalBilling/legalBilling.constants"
import type {
  LegalBillingBatch,
  LegalBillingDateRange,
  LegalBillingInvoiced,
  LegalBillingLine,
  LegalBillingSummary,
  LegalBillingTagGroup,
  LegalBillingTenantGroup,
  LegalBillingTotals,
} from "@/v2-backoffice-client/legalBilling/legalBilling.types"

// The default range: the current calendar month up to today — the invoicing cadence —
// both read in Norwegian time. `today` is injectable so tests don't depend on the wall
// clock.
export function defaultBillingRange(
  today: Date = new Date(),
): LegalBillingDateRange {
  const to = toZonedDateInput(today, NORWAY_TIME_ZONE)
  return { from: `${to.slice(0, 8)}01`, to }
}

// A valid range needs both ends and from <= to. Drives whether the query fires.
export function isValidBillingRange(range: LegalBillingDateRange): boolean {
  if (!range.from || !range.to) return false
  return range.from <= range.to
}

// Converts the date-input values to the inclusive UTC instants the query expects:
// `from` at the start of its Norwegian day, `to` at the very end of it, so a purchase at
// 00:30 Oslo time is invoiced on its own day. Same conversion as the console summary
// (both call zonedDayRangeToUtc): a different day boundary would make the two surfaces
// disagree for the same nominal range.
export function billingRangeToQueryVariables(range: LegalBillingDateRange): {
  from: string
  to: string
} {
  const { from, to } = zonedDayRangeToUtc(
    range.from,
    range.to,
    NORWAY_TIME_ZONE,
  )
  return { from, to }
}

export function isLineInvoiced(line: LegalBillingLine): boolean {
  return Boolean(line.InvoicedAt)
}

// A pending price is real expected spend but not settled: it can still fail and remove
// itself, so any figure containing one is provisional and must be labelled as such rather
// than invoiced.
export function isLineProvisional(line: LegalBillingLine): boolean {
  return line.Status === LEGAL_BILLING_STATUS_PENDING
}

// What is still to be billed at this level. The number the admin is actually after —
// "what did they spend" is only half the question once part of it is already invoiced.
export function outstandingTotal(
  totals: LegalBillingTotals,
  invoiced: LegalBillingInvoiced,
): number {
  return totals.Total - invoiced.Total
}

// --- hiding invoiced rows --------------------------------------------------- //

// Filters an already-invoiced group out of the view, WITHOUT recomputing totals.
//
// Two deliberate decisions, both of which read as bugs if you assume otherwise:
//
//  1. Hiding is opt-in. The default shows everything, because an admin reconciling an
//     invoice needs to see what was already billed — a view that silently omits it turns
//     "already invoiced" into "never purchased".
//  2. Hiding drops whole rows but leaves every Totals/Invoiced figure untouched, so the
//     totals continue to describe the RANGE, not the current filter. A filter that
//     rewrote the totals would let an admin read a smaller number, invoice it, and miss
//     the rest. `outstandingTotal` is what the hidden view is really about, and it stays
//     correct precisely because the totals do not move.
//
// A group is dropped only when it is FULLY invoiced. A half-invoiced batch keeps its
// unbilled lines visible (the billed ones are dropped from the line list), because
// dropping the whole batch would hide work that still needs invoicing.
export function hideInvoicedFromSummary(
  summary: LegalBillingSummary,
): LegalBillingSummary {
  const tenants = summary.Tenants.map(hideInvoicedFromTenant).filter(
    (tenant) => tenant.TagGroups.length > 0,
  )
  return { ...summary, Tenants: tenants }
}

function hideInvoicedFromTenant(
  tenant: LegalBillingTenantGroup,
): LegalBillingTenantGroup {
  const tagGroups = tenant.TagGroups.map(hideInvoicedFromTagGroup).filter(
    (tagGroup) => tagGroup.Batches.length > 0,
  )
  return { ...tenant, TagGroups: tagGroups }
}

function hideInvoicedFromTagGroup(
  tagGroup: LegalBillingTagGroup,
): LegalBillingTagGroup {
  const batches = tagGroup.Batches.map(hideInvoicedFromBatch).filter(
    (batch) => batch.Lines.length > 0,
  )
  return { ...tagGroup, Batches: batches }
}

function hideInvoicedFromBatch(batch: LegalBillingBatch): LegalBillingBatch {
  return {
    ...batch,
    Lines: batch.Lines.filter((line) => !isLineInvoiced(line)),
  }
}

// The ledger rows a mark/unmark should touch for a group AS IT IS CURRENTLY DISPLAYED.
//
// Deliberately derived from the visible batches rather than from the group's
// server-resolved PurchaseIds: with the hide toggle on, the already-invoiced rows are not
// on screen, and acting on rows an admin cannot see is how a mistaken mark happens. Unmark
// is the case that matters — with invoiced rows hidden there is nothing left to unmark, so
// the action falls away instead of silently reversing rows off-screen.
//
// Mark sends only the not-yet-invoiced subset. Re-marking an already-marked row is
// harmless server-side (idempotent), but sending only the unmarked ones keeps the reported
// UpdatedCount meaningful as a staleness signal.
export function uninvoicedPurchaseIds(batches: LegalBillingBatch[]): string[] {
  return batches.flatMap((batch) =>
    batch.Lines.filter((line) => !isLineInvoiced(line)).map(
      (line) => line.PurchaseId,
    ),
  )
}

export function invoicedPurchaseIds(batches: LegalBillingBatch[]): string[] {
  return batches.flatMap((batch) =>
    batch.Lines.filter(isLineInvoiced).map((line) => line.PurchaseId),
  )
}

// The untagged group has no tag string; it is still a real invoicing unit, so it needs a
// label rather than an empty cell.
export function tagGroupLabel(tag: string | null | undefined): string {
  return tag ? tag : "No tag"
}
